/**
 * @file DataSharingCard.tsx
 * @brief Profile → "Data & sharing": the member's "Use my data to improve
 *        models" switch (ADR-048 Part 2, web + Mac), and the company admin's
 *        "Allow members to share data for model improvement" switch.
 *
 * @details
 * Both render the server's wording as is, a switch and a "Privacy policy"
 * link. Default off. Layout: the GridBand + row pattern of
 * NotificationSettings. The Mac app opens the link in the browser
 * (DESKTOP main.js window-open handler).
 *
 * @author DES (prepared with Claude)
 * @date 20-10-2026
 */

import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/Switch";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useCompanyModelTraining, useModelTraining, useSaveCompanyModelTraining, useSaveModelTraining } from "./api";
import { memberToggleView, saveErrorMessage } from "./modelTraining";
import { AiConsentRow } from "@/features/ai-consent/AiConsentRow";

function PrivacyLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-1 inline-block text-xs font-medium text-brand hover:underline"
    >
      Privacy policy
    </a>
  );
}

function errorText(err: unknown) {
  return saveErrorMessage(err instanceof ApiClientError ? err : null);
}

/** Member's own choice, on the Profile tab. */
export function DataSharingCard({ isCompanyAdmin = false }: { isCompanyAdmin?: boolean }) {
  const q = useModelTraining();
  const save = useSaveModelTraining();
  const data = q.data;

  const onChange = async (optIn: boolean) => {
    if (!data) return;
    try {
      await save.mutateAsync({ optIn, version: data.wording.version });
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: errorText(err) });
    }
  };

  const view = data ? memberToggleView(data) : null;

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">Data &amp; sharing</GridBand>
      {!data || !view ? (
        <div className="px-4 py-3.5 text-sm text-muted-foreground md:px-5">
          {q.isError ? "Could not load your data sharing choice." : "Loading…"}
        </div>
      ) : (
        <div className="flex items-start gap-3 border-b border-border/60 px-4 py-3.5 md:px-5">
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">{data.wording.title}</span>
            <span className="block text-xs text-muted-foreground">{data.wording.body}</span>
            {view.caption ? (
              <span className="mt-1 block text-xs font-medium text-muted-foreground">
                {view.caption}
                {isCompanyAdmin ? (
                  <>
                    {" · "}
                    <Link to="/company/settings" className="text-brand hover:underline">
                      Company settings
                    </Link>
                  </>
                ) : null}
              </span>
            ) : null}
            <PrivacyLink href={data.wording.privacyUrl} />
          </span>
          <Switch
            checked={view.checked}
            disabled={view.disabled || save.isPending}
            onCheckedChange={(v: boolean) => void onChange(v)}
            aria-label={data.wording.title}
            className="mt-0.5"
          />
        </div>
      )}
      {/* "How VERBLIZR uses AI" (B7, 22-10-2026): when it was agreed. */}
      <AiConsentRow />
    </div>
  );
}

/** Company admin's switch, on Company settings. */
export function CompanyDataSharingSwitch() {
  const q = useCompanyModelTraining();
  const save = useSaveCompanyModelTraining();
  const data = q.data;

  const onChange = async (allowMembers: boolean) => {
    try {
      await save.mutateAsync({ allowMembers });
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: errorText(err) });
    }
  };

  if (!data) {
    return (
      <div className="border-b border-border/60 px-4 py-3.5 text-sm text-muted-foreground md:px-5">
        {q.isError ? "Could not load the data sharing setting." : "Loading…"}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 border-b border-border/60 px-4 py-3.5 md:px-5">
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{data.wording.title}</span>
        <span className="block text-xs text-muted-foreground">{data.wording.body}</span>
        <PrivacyLink href={data.wording.privacyUrl} />
      </span>
      <Switch
        checked={data.allowMembers}
        disabled={save.isPending}
        onCheckedChange={(v: boolean) => void onChange(v)}
        aria-label={data.wording.title}
        className="mt-0.5"
      />
    </div>
  );
}
